// Three-column OKR cascade: Enterprise Objective → Key Results → Active Projects.

import { useOptionalRichTooltipController } from '../richTooltips/RichTooltipProvider.jsx';

export function OkrAlignmentTree({ objective, keyResults = [], projects = [] }) {
  const tooltip = useOptionalRichTooltipController();
  return (
    <section className="okr-tree">
      <div className="okr-col okr-objective">
        <div className="eyebrow" style={{ color: 'rgba(255,255,255,0.7)' }}>
          Enterprise objective
        </div>
        <h3>{objective?.name || objective?.title || 'Mission objective'}</h3>
        {objective?.summary ? <p>{objective.summary}</p> : null}
        {objective?.targetDate ? (
          <span className="chip outline sm">Target {objective.targetDate}</span>
        ) : null}
      </div>
      <div className="okr-col okr-krs">
        <div className="eyebrow">Key results</div>
        <ul>
          {keyResults.map((kr, i) => {
            const pct = Math.max(0, Math.min(100, Number(kr.progress || 0)));
            const triggerProps =
              kr.richTooltip && tooltip
                ? tooltip.getTriggerProps({
                    surfaceId: kr.richTooltip.surfaceId,
                    payload: kr.richTooltip,
                    testIdPrefix: 'rich-tooltip',
                    ariaLabel: `${kr.name || kr.title} key result details`,
                  })
                : {};
            return (
              <li
                key={kr.id || i}
                className={kr.richTooltip ? 'okr-kr-rich' : ''}
                role={kr.richTooltip ? 'button' : undefined}
                tabIndex={kr.richTooltip ? 0 : undefined}
                data-testid={`okr-kr-${kr.id || i}`}
                {...triggerProps}
              >
                <span className="okr-kr-badge">KR{i + 1}</span>
                <div className="stack tight" style={{ flex: 1 }}>
                  <strong>{kr.name || kr.title}</strong>
                  <div className="row" style={{ gap: 8 }}>
                    <div className="score-bar" style={{ width: 120 }}>
                      <i style={{ width: `${pct}%` }} />
                    </div>
                    <span className="mono small">{pct}%</span>
                  </div>
                  {kr.target ? <span className="muted small">Target {kr.target}</span> : null}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
      <div className="okr-col okr-projects">
        <div className="eyebrow">Active projects</div>
        <ul>
          {projects.map((p) => (
            <li
              key={p.id}
              className={`okr-project tone-${p.healthTone || 'blue'}${p.richTooltip ? ' okr-project-rich' : ''}`}
              role={p.richTooltip ? 'button' : undefined}
              tabIndex={p.richTooltip ? 0 : undefined}
              data-testid={`okr-project-${p.id}`}
              {...(p.richTooltip && tooltip
                ? tooltip.getTriggerProps({
                    surfaceId: p.richTooltip.surfaceId,
                    payload: p.richTooltip,
                    testIdPrefix: 'rich-tooltip',
                    ariaLabel: `${p.name} project alignment details`,
                  })
                : {})}
            >
              <span className="okr-project-dot" aria-hidden />
              <div className="stack tight" style={{ flex: 1 }}>
                <strong>{p.name}</strong>
                <span className="muted small">
                  {p.linked ? '↔ aligned' : '⤬ misaligned'} ·{' '}
                  {String(p.status || 'in flight')
                    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
                    .replace(/[_-]+/g, ' ')
                    .toLowerCase()}
                </span>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
